import React, { useState } from 'react';
import { subDays, startOfDay, endOfDay, parseISO } from 'date-fns';

export interface SessionFilters {
  dateRange: 'today' | 'yesterday' | 'last7days' | 'custom';
  startDate?: string;
  endDate?: string;
  driverId?: string;
  busId?: string;
}

interface SessionFiltersProps {
  initialFilters?: SessionFilters;
  onChange: (filters: SessionFilters) => void;
  onApply: () => void;
  onClear: () => void;
  isLoading?: boolean;
}

export const SessionFilters: React.FC<SessionFiltersProps> = ({
  initialFilters,
  onChange,
  onApply,
  onClear,
  isLoading = false,
}) => {
  const [dateRange, setDateRange] = useState<'today' | 'yesterday' | 'last7days' | 'custom'>(
    initialFilters?.dateRange || 'last7days'
  );
  const [customStartDate, setCustomStartDate] = useState<string>(
    initialFilters?.startDate ? new Date(initialFilters.startDate).toISOString().split('T')[0] : ''
  );
  const [customEndDate, setCustomEndDate] = useState<string>(
    initialFilters?.endDate ? new Date(initialFilters.endDate).toISOString().split('T')[0] : ''
  );
  const [driverSearch, setDriverSearch] = useState<string>(initialFilters?.driverId || '');
  const [busSearch, setBusSearch] = useState<string>(initialFilters?.busId || '');

  // Calculate date range based on selection
  const getDateRange = (option: 'today' | 'yesterday' | 'last7days' | 'custom') => {
    const now = new Date();

    switch (option) {
      case 'today':
        return {
          start: startOfDay(now).toISOString(),
          end: endOfDay(now).toISOString(),
        };
      case 'yesterday':
        const yesterday = subDays(now, 1);
        return {
          start: startOfDay(yesterday).toISOString(),
          end: endOfDay(yesterday).toISOString(),
        };
      case 'last7days':
        return {
          start: startOfDay(subDays(now, 7)).toISOString(),
          end: endOfDay(now).toISOString(),
        };
      case 'custom':
        return {
          start: customStartDate ? parseISO(`${customStartDate}T00:00:00`).toISOString() : '',
          end: customEndDate ? parseISO(`${customEndDate}T23:59:59`).toISOString() : '',
        };
      default:
        return { start: '', end: '' };
    }
  };

  // Validate filters
  const validateFilters = (): string | null => {
    if (dateRange === 'custom') {
      if (!customStartDate || !customEndDate) {
        return 'Veuillez sélectionner une date de début et de fin';
      }
      const startDate = parseISO(`${customStartDate}T00:00:00`);
      const endDate = parseISO(`${customEndDate}T23:59:59`);
      if (endDate < startDate) {
        return 'La date de fin doit être après la date de début';
      }
    }
    return null;
  };

  // Handle apply filters
  const handleApply = () => {
    const error = validateFilters();
    if (error) {
      alert(error);
      return;
    }

    const { start, end } = getDateRange(dateRange);

    const filters: SessionFilters = {
      dateRange,
      ...(start && { startDate: start }),
      ...(end && { endDate: end }),
      ...(driverSearch && { driverId: driverSearch }),
      ...(busSearch && { busId: busSearch }),
    };

    onChange(filters);
    onApply();
  };

  // Handle clear filters
  const handleClear = () => {
    setDateRange('last7days');
    setCustomStartDate('');
    setCustomEndDate('');
    setDriverSearch('');
    setBusSearch('');

    const { start, end } = getDateRange('last7days');
    onChange({
      dateRange: 'last7days',
      startDate: start,
      endDate: end,
    });

    onClear();
  };

  const dateRangeOptions = [
    { value: 'today' as const, label: "Aujourd'hui" },
    { value: 'yesterday' as const, label: 'Hier' },
    { value: 'last7days' as const, label: '7 derniers jours' },
    { value: 'custom' as const, label: 'Période personnalisée' },
  ];

  return (
    <div className="bg-white p-4 rounded-lg shadow mb-4 border border-gray-200">
      <h3 className="text-lg font-semibold mb-4 text-gray-900">Filtres</h3>

      {/* Date Range Selection */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Période</label>
          <select
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value as any)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {dateRangeOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        {/* Custom Date Inputs - shown only if custom selected */}
        {dateRange === 'custom' && (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Date de début</label>
              <input
                type="date"
                value={customStartDate}
                onChange={(e) => setCustomStartDate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Date de fin</label>
              <input
                type="date"
                value={customEndDate}
                onChange={(e) => setCustomEndDate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </>
        )}
      </div>

      {/* Search Inputs */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Driver ID</label>
          <input
            type="text"
            placeholder="Rechercher driver..."
            value={driverSearch}
            onChange={(e) => setDriverSearch(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Bus ID</label>
          <input
            type="text"
            placeholder="Rechercher bus..."
            value={busSearch}
            onChange={(e) => setBusSearch(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex gap-2 justify-end">
        <button
          onClick={handleClear}
          disabled={isLoading}
          className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 disabled:opacity-50 transition font-medium"
        >
          Réinitialiser
        </button>
        <button
          onClick={handleApply}
          disabled={isLoading}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 transition font-medium"
        >
          {isLoading ? 'Chargement...' : 'Appliquer'}
        </button>
      </div>
    </div>
  );
};
